(function () {
  const CLOSED_KEY = 'eb:popup:closed_until';
  const SHOWN_KEY = 'eb:popup:shown';

  class EbPopup extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.eb-popup__card');
      this.bubble = this.querySelector('[data-popup-bubble]');
      this.displayStyle = this.dataset.displayStyle || 'direct';
      this.collapseOnClose = this.dataset.collapseOnClose === 'true' && Boolean(this.bubble);
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.boundSectionSelect = (event) => {
        if (this.isOwnSectionEvent(event)) this.showConfiguredState();
        else this.close(false);
      };
      this.boundSectionDeselect = (event) => {
        if (this.isOwnSectionEvent(event)) this.close(false);
      };

      if (this.shouldSkip()) return;
      this.bind();
      this.bindThemeEditor();
      if (this.isDesignMode) {
        // Mirror the storefront state in the editor: bubble and card are mutually exclusive.
        window.setTimeout(() => this.showConfiguredState(), 80);
        return;
      }
      this.armTrigger();
      this.observeSuccess();
    }

    disconnectedCallback() {
      if (!this.isDesignMode) return;
      document.removeEventListener('shopify:section:select', this.boundSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.boundSectionDeselect);
    }

    bind() {
      this.addEventListener('click', (event) => {
        // The bubble's own × is the only permanent dismissal when the popup collapses.
        if (event.target.closest('[data-popup-bubble-dismiss]')) {
          event.stopPropagation();
          this.dismissBubble();
          return;
        }
        if (event.target.closest('[data-popup-bubble]')) {
          this.expandFromBubble();
          return;
        }
        if (event.target.closest('[data-popup-close]')) this.close(true);
        if (event.target.closest('[data-popup-copy]')) this.copyCode(event.target.closest('[data-popup-copy]'));
      });

      this.bubble?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (!event.target.closest('[data-popup-bubble-dismiss]')) return;
        event.preventDefault();
        event.stopPropagation();
        this.dismissBubble();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close(true);
      });

      this.bindDrag();
      this.bindBirthday();
    }

    // On submit, fold the optional birthday into the customer tags
    // (Shopify's customer form has no birthday field). n8n parses the
    // `bday:MM-DD` tag from the customers/create webhook and writes it
    // into a customer metafield for future birthday rewards.
    bindBirthday() {
      const form = this.querySelector('.eb-popup__form');
      const birthday = this.querySelector('[data-popup-birthday]');
      if (!form || !birthday) return;
      form.addEventListener('submit', () => {
        const month = birthday.querySelector('[data-bday-month]')?.value;
        const day = birthday.querySelector('[data-bday-day]')?.value;
        const tagsInput = form.querySelector('input[name="contact[tags]"]');
        if (!month || !day || !tagsInput) return;
        const bdayTag = `bday:${month}-${day}`;
        const tags = tagsInput.value.split(',').map((tag) => tag.trim()).filter(Boolean).filter((tag) => !tag.startsWith('bday:'));
        tags.push(bdayTag);
        tagsInput.value = tags.join(',');

        // Also hand the birthday straight to the discount app, so a voucher can
        // be issued without waiting on the tag -> webhook -> metafield trip.
        // The customer tag above is written either way: this is an addition,
        // never a replacement, and a failure here must not affect the signup.
        this.sendBirthday(form, month, day);
      });
    }

    // Fire-and-forget. NOT awaited, and its failure is swallowed on purpose:
    // the Shopify customer form is the request that must not fail, and it is
    // about to navigate away.
    sendBirthday(form, month, day) {
      const endpoint = this.dataset.subscribeEndpoint;
      if (!endpoint) return;                       // left empty = switched off
      const email = form.querySelector('[name="contact[email]"]')?.value?.trim();
      if (!email) return;
      try {
        // keepalive: the native submit navigates away immediately, and a plain
        // fetch would be cut off mid-flight — the birthday would just vanish.
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            email,
            birthday_month: month,
            birthday_day: day,
            source: 'popup',
            locale: document.documentElement.lang || 'en',
          }),
        }).catch(() => {});
      } catch (e) { /* same reason */ }
    }

    bindThemeEditor() {
      if (!this.isDesignMode) return;
      document.addEventListener('shopify:section:select', this.boundSectionSelect);
      document.addEventListener('shopify:section:deselect', this.boundSectionDeselect);
    }

    isOwnSectionEvent(event) {
      const eventSectionId = event.detail?.sectionId;
      if (eventSectionId && eventSectionId === this.dataset.sectionId) return true;

      const sectionRoot = this.closest('[id^="shopify-section-"]');
      return Boolean(sectionRoot && event.target === sectionRoot);
    }

    shouldSkip() {
      if (this.isDesignMode) return false;
      // The bubble is a standing entry point, not a one-shot interruption: it comes back
      // on every load so the offer stays reachable. Only its × (or a successful signup,
      // which writes CLOSED_KEY) retires it. A card popup keeps the once-per-session rule.
      if (this.displayStyle !== 'bubble' && this.storageGet(SHOWN_KEY, true)) return true;
      const closedUntil = Number(this.storageGet(CLOSED_KEY) || 0);
      return closedUntil > Date.now();
    }

    armTrigger() {
      const trigger = this.dataset.trigger || 'scroll';
      if (trigger === 'delay') {
        window.setTimeout(() => this.triggerReached(), Number(this.dataset.delay || 10) * 1000);
      } else if (trigger === 'exit' && window.matchMedia('(min-width: 768px)').matches) {
        document.addEventListener('mouseleave', (event) => {
          if (event.clientY <= 0) this.triggerReached();
        }, { once: true });
      } else {
        const onScroll = () => {
          const depth = Number(this.dataset.scrollDepth || 50) / 100;
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          if (maxScroll > 0 && window.scrollY / maxScroll >= depth) {
            window.removeEventListener('scroll', onScroll);
            this.triggerReached();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    // In bubble mode the trigger reveals the collapsed pill instead of the card.
    triggerReached() {
      if (this.displayStyle === 'bubble') this.showBubble();
      else this.openWhenAvailable();
    }

    showConfiguredState() {
      if (this.displayStyle === 'bubble' && this.bubble) {
        this.close(false);
        this.showBubble();
        return;
      }
      this.open();
    }

    showBubble() {
      if (!this.bubble || this.classList.contains('is-open')) return;
      window.clearTimeout(this.bubbleHideTimer);
      this.bubble.hidden = false;
      this.bubble.classList.remove('is-hiding');
      this.setAttribute('aria-hidden', 'false');
    }

    hideBubble(immediate = false) {
      if (!this.bubble || this.bubble.hidden) return;
      window.clearTimeout(this.bubbleHideTimer);
      if (immediate) {
        this.bubble.hidden = true;
        this.bubble.classList.remove('is-hiding');
        if (!this.classList.contains('is-open')) this.setAttribute('aria-hidden', 'true');
        return;
      }
      this.bubble.classList.add('is-hiding');
      this.bubbleHideTimer = window.setTimeout(() => {
        this.bubble.hidden = true;
        this.bubble.classList.remove('is-hiding');
        this.bubbleHideTimer = null;
        if (!this.classList.contains('is-open')) this.setAttribute('aria-hidden', 'true');
      }, 200);
    }

    expandFromBubble() {
      this.hideBubble();
      window.setTimeout(() => this.open(), 180);
    }

    // Closing the bubble is a real "not interested" — respect frequency_days.
    dismissBubble() {
      this.hideBubble();
      if (this.isDesignMode) return;
      const days = Number(this.dataset.frequencyDays || 15);
      this.storageSet(CLOSED_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
    }

    openWhenAvailable() {
      if (this.overlayOpen()) {
        if (!this.awaitingOverlayClose) {
          this.awaitingOverlayClose = true;
          const retry = () => {
            this.awaitingOverlayClose = false;
            window.setTimeout(() => this.openWhenAvailable(), 120);
          };
          ['eb:cart:close', 'eb:quick-add:close', 'eb:search:close', 'eb:facets:close', 'eb:compare:close', 'eb:comparison-picker:close'].forEach((name) => {
            document.addEventListener(name, retry, { once: true });
          });
        }
        return;
      }
      this.open();
    }

    overlayOpen() {
      return Boolean(
        document.body.classList.contains('eb-product-drawer-open') ||
        document.querySelector('.eb-cart-drawer.is-open, .eb-search-overlay.is-open, .eb-quick-add.is-open')
      );
    }

    open() {
      // The bubble is the collapsed state, so it must never remain beside an open card.
      this.hideBubble(true);
      if (this.classList.contains('is-open')) return;
      if (this.isDesignMode) this.openScrollY = window.scrollY;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.isDesignMode) return;
      window.ebScrollLock?.lock();
      // In bubble style the card is opened by the visitor, so it needs no session guard —
      // it always folds back into the bubble it came from.
      if (this.displayStyle !== 'bubble') this.storageSet(SHOWN_KEY, '1', true);
    }

    close(persist) {
      const wasOpen = this.classList.contains('is-open');
      const restoreScrollY = this.isDesignMode ? this.openScrollY : null;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (wasOpen && !this.isDesignMode) window.ebScrollLock?.unlock();

      // With collapse enabled the card folds back into the bubble and the offer stays
      // reachable, so closing the card is not yet a dismissal.
      const collapses = this.collapseOnClose && !this.querySelector('[data-popup-success]');
      if (collapses && wasOpen) {
        window.setTimeout(() => this.showBubble(), this.isDesignMode ? 0 : 240);
      }

      if (persist && !collapses && !this.isDesignMode) {
        const days = Number(this.dataset.frequencyDays || 15);
        this.storageSet(CLOSED_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
      }
      if (wasOpen) this.dispatchEvent(new CustomEvent('eb:popup:close', { bubbles: true }));
      if (wasOpen && restoreScrollY !== undefined) this.restoreEditorScroll(restoreScrollY);
    }

    restoreEditorScroll(scrollY) {
      if (!this.isDesignMode || typeof scrollY !== 'number') return;
      const restore = () => window.scrollTo({ left: window.scrollX, top: scrollY, behavior: 'auto' });
      restore();
      window.requestAnimationFrame(restore);
      window.setTimeout(restore, 80);
    }

    async copyCode(button) {
      const code = this.querySelector('[data-popup-code] code')?.textContent?.trim();
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (error) {
        window.prompt('', code);
      }
      // Update only the label span so the icon survives; toggle ticket success state.
      const label = button.querySelector('span') || button;
      const original = label.textContent;
      label.textContent = this.dataset.copiedLabel || original;
      const ticket = button.closest('[data-popup-code]');
      ticket?.classList.add('is-copied');
      window.setTimeout(() => {
        label.textContent = original;
        ticket?.classList.remove('is-copied');
      }, 1200);
    }

    observeSuccess() {
      if (!this.querySelector('[data-popup-success]')) return;
      // The teaser has done its job — the real code ticket replaces it.
      this.querySelectorAll('[data-popup-offer-teaser]').forEach((node) => node.remove());
      this.bubble?.remove();
      this.bubble = null;
      this.collapseOnClose = false;
      this.open();
      if (this.isDesignMode) return;
      this.storageSet(CLOSED_KEY, String(Date.now() + 365 * 24 * 60 * 60 * 1000));
      if (this.dataset.mode !== 'offer') window.setTimeout(() => this.close(false), 2500);
    }

    bindDrag() {
      let startY = 0;
      let active = false;
      this.card?.addEventListener('pointerdown', (event) => {
        if (!window.matchMedia('(max-width: 640px)').matches) return;
        startY = event.clientY;
        active = true;
      });
      this.card?.addEventListener('pointerup', (event) => {
        if (!active) return;
        active = false;
        if (event.clientY - startY > 70) this.close(true);
      });
    }

    storageGet(key, sessionOnly) {
      try {
        return (sessionOnly ? sessionStorage : localStorage).getItem(key);
      } catch (error) {
        try {
          return sessionStorage.getItem(key);
        } catch (sessionError) {
          return null;
        }
      }
    }

    storageSet(key, value, sessionOnly) {
      try {
        (sessionOnly ? sessionStorage : localStorage).setItem(key, value);
      } catch (error) {
        try {
          sessionStorage.setItem(key, value);
        } catch (sessionError) {}
      }
    }
  }

  if (!customElements.get('eb-popup')) customElements.define('eb-popup', EbPopup);
})();
